"use client";

import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { AlertCircleIcon, DownloadIcon, Undo2Icon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGeneratedDocumentStore } from "@/components/document/generatedDocumentStore";
import { apiAuthFetch } from "@/lib/authFetch";
import { Spinner } from "@/components/ui/spinner";
import { openGeneratedDocumentInSidebar } from "@/lib/threadDocumentOps";
import { getLiveThreadId } from "@/components/assistant";
import { SaveOrReviseButton } from "@/components/document/SaveOrReviseButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/** Skeleton placeholder matching the document block shape. */
function DocumentSkeleton({ title }: { title?: string }) {
  const { t } = useTranslation();
  const label = title
    ? t("documentGenerator.generatingTitled", { title })
    : t("documentGenerator.generating");

  return (
    <div className="my-4 rounded-lg border border-border overflow-hidden animate-pulse">
      <div className="flex items-center gap-2 bg-muted/50 px-3 py-1.5">
        <Spinner size="sm" className="text-muted-foreground" />
        <span className="text-xs text-muted-foreground truncate">{label}</span>
      </div>
      <div className="px-4 py-3.5">
        <div className="h-4 w-48 rounded bg-muted-foreground/15" />
      </div>
    </div>
  );
}

const UNDO_WINDOW_MS = 10_000;
const EMPTY_VERSIONS: { versionId: string; generatedAt?: string | null }[] = [];

/**
 * Hook that manages the 10-second undo window for a document.
 * Returns { canUndo, secondsLeft, performUndo, undoing }.
 * The window starts from generatedAt of the latest version.
 */
function useUndoWindow(docId: string, versions: { versionId: string; generatedAt?: string | null }[]) {
  const [canUndo, setCanUndo] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [undoing, setUndoing] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const removeLatestVersion = useGeneratedDocumentStore((s) => s.removeLatestVersion);

  useEffect(() => {
    if (versions.length <= 1) {
      setCanUndo(false);
      return;
    }
    const latest = versions[versions.length - 1];
    if (!latest?.generatedAt) {
      setCanUndo(false);
      return;
    }
    const createdMs = new Date(latest.generatedAt).getTime();
    const remaining = () => Math.max(0, UNDO_WINDOW_MS - (Date.now() - createdMs));

    const r = remaining();
    if (r <= 0) {
      setCanUndo(false);
      return;
    }

    setCanUndo(true);
    setSecondsLeft(Math.ceil(r / 1000));

    timerRef.current = setInterval(() => {
      const left = remaining();
      if (left <= 0) {
        setCanUndo(false);
        setSecondsLeft(0);
        if (timerRef.current) clearInterval(timerRef.current);
      } else {
        setSecondsLeft(Math.ceil(left / 1000));
      }
    }, 250);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [versions]);

  const performUndo = useCallback(async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (undoing) return;
    setUndoing(true);
    try {
      const res = await apiAuthFetch(`/api/documents/${docId}/versions/latest`, { method: "DELETE" });
      if (res.ok) {
        const body = await res.json() as { removedVersionId?: string; currentVersionId?: string };
        removeLatestVersion(docId, body.removedVersionId ?? "", body.currentVersionId);
        setCanUndo(false);
      }
    } finally {
      setUndoing(false);
    }
  }, [docId, removeLatestVersion, undoing]);

  return { canUndo, secondsLeft, performUndo, undoing };
}

/**
 * Inline generated-document card. Rendered in the message footer for any assistant
 * turn that produced documents (driven by `AssistantThreadMessage.GeneratedDocumentIds`)
 * and as the cell for entries in the thread-wide `ThreadDocumentsPanel`.
 *
 * "Ready" is a pure store-status check now (`entry.status === "ready"`); the underlying
 * blob's existence is verified by the download endpoint, not pre-checked here. Click the
 * card body to open the doc in the unified PDF sidebar — auto-open also fires on
 * `data-document-ready`, so this path matters for the "user closed the sidebar and wants
 * it back" case and for cold reloads from history.
 */
export const DocumentBlock: FC<{ docId: string; isUpdate?: boolean }> = ({ docId, isUpdate }) => {
  const { t } = useTranslation();
  const entry = useGeneratedDocumentStore((s) => s.entries[docId]);

  const [downloading, setDownloading] = useState<"pdf" | "docx" | null>(null);

  const status = entry?.status;
  const isReady = status === "ready";
  const title = entry?.title ?? `document-${docId}`;

  const { canUndo, secondsLeft, performUndo, undoing } = useUndoWindow(docId, entry?.versions ?? EMPTY_VERSIONS);

  const handleDownload = useCallback(
    async (format: "pdf" | "docx", e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (downloading) return;
      setDownloading(format);
      try {
        const res = await apiAuthFetch(`/api/documents/${docId}/download?format=${format}`);

        if (
          res.status === 503 &&
          res.headers.get("X-Conversion-Failed") === "1"
        ) {
          const { usePdfErrorStore } = await import("@/lib/stores/pdfErrorStore");
          usePdfErrorStore.getState().show(
            "documents.conversionServiceUnavailable",
            { title },
            () => { void handleDownload(format); },
          );
          return;
        }

        if (res.status === 406) {
          const { toast } = await import("sonner");
          toast.error(t("documents.wordFormatUnavailable"));
          return;
        }

        if (!res.ok) {
          const { toast } = await import("sonner");
          toast.error(t("documents.downloadFailed"));
          return;
        }

        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${title}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        const { ClientLogger } = await import("@/lib/clientLogger");
        ClientLogger.error("document-generator download failed", { docId, format, err });
        const { toast } = await import("sonner");
        toast.error(t("documents.downloadFailed"));
      } finally {
        setDownloading(null);
      }
    },
    [docId, downloading, title, t],
  );

  const handleOpenInSidebar = useCallback(() => {
    if (!isReady) return;
    void openGeneratedDocumentInSidebar(docId, entry?.title, getLiveThreadId());
  }, [docId, entry?.title, isReady]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleOpenInSidebar();
      }
    },
    [handleOpenInSidebar],
  );

  if (!entry || status === "rendering" || (isUpdate && entry?.updatingVersion)) {
    return <DocumentSkeleton title={entry?.title} />;
  }

  if (status === "error") {
    return (
      <div className="my-4 rounded-lg border border-border overflow-hidden">
        <div
          className="flex items-center gap-2 bg-muted/50 px-3 py-2 text-xs text-destructive"
          title={entry.error ?? undefined}
        >
          <AlertCircleIcon className="size-3" />
          <span>{t("documentGenerator.generationFailed")}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleOpenInSidebar}
      onKeyDown={handleKeyDown}
      className="my-4 flex items-center gap-3 rounded-lg border border-border px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors animate-in fade-in slide-in-from-bottom-1 duration-300"
    >
      <img src="/docx-icon.png" alt="DOCX Icon" className="size-13 p-0.5" />
      <div className="flex-1 min-w-0">
        <span className="font-body text-sm font-medium truncate block">{title}</span>
        {entry.versions.length > 0 && (
          <span className="text-[10px] text-muted-foreground">
            {entry.versions.length > 1
              ? t("docStepper.cardStats.versioned", { version: entry.versions.length })
              : t("docStepper.cardStats.created")}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        {canUndo && (
          <button
            type="button"
            onClick={performUndo}
            disabled={undoing}
            className="flex h-7 items-center gap-1.5 rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 px-2.5 text-xs text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors disabled:opacity-50 cursor-pointer animate-in fade-in duration-200"
          >
            {undoing ? <Spinner size="sm" /> : <Undo2Icon className="size-3.5" />}
            <span>{t("docStepper.undo.button", { seconds: secondsLeft })}</span>
          </button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={downloading !== null}
              className="flex h-7 items-center gap-1.5 rounded-md border border-border/60 bg-background px-2.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50 cursor-pointer"
            >
              {downloading ? <Spinner size="sm" /> : <DownloadIcon className="size-3.5" />}
              <span>{t("documentGenerator.downloadPdfShort")}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleDownload("pdf")}>
              {t("documentGenerator.downloadPdf")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleDownload("docx")}>
              {t("documentGenerator.downloadDocx")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <SaveOrReviseButton docId={docId} compact />
      </div>
    </div>
  );
};
